import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useGoogleLogin } from '@react-oauth/google';
import { Button, Checkbox, Col, Divider, Input, Modal, Row, Tooltip } from 'antd';
import { LoginSocialApple } from 'reactjs-social-login';

import AuthForm from '../../components/AuthForm/AuthForm';
import AuthInfo from '../../components/AuthInfo/AuthInfo';

import { Apple, CloseIcon, Google, Hand, Info } from '../../assets/icons';
import { Email } from '../../assets/icons/Email';

import { resetUserData } from '../../store/reducer/accountSlice';
import { realBricksApi } from '../../utils/axiosInterceptors';
import { formatePhoneNumber } from '../../utils/formatPhoneNumber';
import { toastNotify } from '../../utils/toastNotify';
import { FIRST_NAME_LENGTHS, FIRST_NAME_VALIDATION, LAST_NAME_LENGTHS, LAST_NAME_VALIDATION } from '../../utils/validationMessages';
import { alphabetSpaceRegex, onlyAlphabetRegex, phoneRegex } from '../../utils/Validations';

import './SignupAuth.scss';

export const SignupAuth = () => {
  const [viewForm, setViewForm] = useState(false);
  const [disclosureOpen, setDisclosureOpen] = useState(false);
  const [newModalVisible, setNewModalVisible] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [referredBy, setReferredBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [userResponseGoogle, setUserResponseGoogle] = useState(null);
  const [responseApple, setResponseApple] = useState(null);
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [referralCodeError, setReferralCodeError] = useState('');
  const [subscribeForSMS, setSubscribeForSMS] = useState(false);
  const userProfileData = useSelector((state) => state.userData.userData);

  const dispatch = useDispatch();
  let navigate = useNavigate();
  const shouldLog = useRef(true);
  const search = useLocation().search;
  const referralLink = new URLSearchParams(search).get('referredBy');

  const [socialDataLoaded, setSocialDataLoaded] = useState(false);
  const [dataManuallyEdited, setDataManuallyEdited] = useState(false);

  const setSocialNameData = (data) => {
    setFirstName(data.firstName || '');
    setLastName(data.lastName || '');
    setPhoneNumber(data.phoneNumber || '');
    setReferredBy(data.referredBy || '');
    setSocialDataLoaded(true);
  };

  const signupWithEmailUrl = referralLink ? `/sign-up?referredBy=${referralLink}` : '/sign-up';

  useEffect(() => {
    if (shouldLog.current) {
      shouldLog.current = false;
      if (Object.keys(userProfileData).length && localStorage.getItem('access_token')) {
        navigate('/');
        toastNotify('error', 'You are already signed in. Please sign out in order to sign-up with different account.');
      }
    }
  }, []);

  useEffect(() => {
    validateFormData();
  }, [setSocialNameData]);

  useEffect(() => {
    if (referralLink) {
      setReferredBy(referralLink);
    }
  }, [referralLink]);

  // Function to handle validation
  const validateFormData = () => {
    if (dataManuallyEdited || !firstName.trim() || !lastName.trim() || !phoneNumber.trim() || !referredBy.trim()) {
      handleFirstNameChange({ target: { value: firstName } });
      handleLastNameChange({ target: { value: lastName } });
      handlePhoneNumberChange({ target: { value: phoneNumber } });
      handleReferralCodeChange({ target: { value: referredBy } });
    }
  };

  const openNewSocialModal = () => {
    const isPinNotSetGoogleLogin = !userResponseGoogle?.data?.data?.isPinSet;
    const isPinNotSetAppleLogin = !responseApple?.data?.data?.isPinSet;
    if (isPinNotSetGoogleLogin || isPinNotSetAppleLogin) {
      setNewModalVisible(true);
    }
  };

  const handleChangePhoneNumber = (e) => {
    const formattedNumber = formatePhoneNumber(e.target.value);
    setPhoneNumber(formattedNumber);
  };

  const closeNewSocialModal = () => {
    setFirstName('');
    setLastName('');
    setFirstNameError('');
    setLastNameError('');
    setNewModalVisible(false);
  };

  const handleDoneClick = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem('access_token');
      const payload = {
        firstName: firstName?.trim(),
        lastName,
      };

      // Include phone number in payload if it's not empty
      if (phoneNumber.trim() !== '') {
        const phoneNumberInp = `${phoneNumber.slice(1, 4)}${phoneNumber.slice(6, 9)}${phoneNumber.slice(10, 14)}`;
        payload.phoneNumber = phoneNumberInp;
      }

      // Include referral code in payload if it's not empty
      if (referredBy.trim() !== '') {
        payload.referredBy = referredBy.trim();
      }

      // Include referral code from URL if available
      if (referralLink) {
        payload.referredBy = referralLink;
      }

      if (!subscribeForSMS) {
        payload.subscribeForSMS = false;
      }

      if (subscribeForSMS && phoneNumber.trim() !== '') {
        payload.subscribeForSMS = subscribeForSMS;
      }
      const apiResponse = await realBricksApi.put(`${process.env.REACT_APP_API_URL}/api/v1/user/social-name`, payload, {
        headers: {
          Authorization: `${accessToken}`,
        },
      });

      localStorage.setItem('email', apiResponse?.data?.data?.email);
      setNewModalVisible(false);
      navigate('/sign-up/createpin');
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  const handleFirstNameChange = (e) => {
    const value = e.target.value;

    setFirstName(value);
    setDataManuallyEdited(true);

    if (!value.trim()) {
      setFirstNameError(FIRST_NAME_VALIDATION.REQUIRED);
    } else if (value?.trim().length < FIRST_NAME_LENGTHS.MIN || value?.trim().length > FIRST_NAME_LENGTHS.MAX) {
      setFirstNameError(FIRST_NAME_VALIDATION.MIN_MAX_LENGTH);
    } else if (!alphabetSpaceRegex.test(value)) {
      setFirstNameError(FIRST_NAME_VALIDATION.FORMAT);
    } else {
      setFirstNameError('');
    }
  };

  const handleReferralCodeChange = (e) => {
    const value = e.target.value;
    setReferredBy(value);
    setDataManuallyEdited(true);

    if (value.trim()) {
      if (value.length < 2 || value.length > 30) {
        setReferralCodeError('Referral code must be between 2 and 30 characters.');
      } else {
        setReferralCodeError('');
      }
    } else {
      setReferralCodeError('');
    }
  };

  const handleLastNameChange = (e) => {
    const value = e.target.value;

    setLastName(value);
    setDataManuallyEdited(true);

    if (!value.trim()) {
      setLastNameError(LAST_NAME_VALIDATION.REQUIRED);
    } else if (value.length < LAST_NAME_LENGTHS.MIN || value.length > LAST_NAME_LENGTHS.MAX) {
      setLastNameError(LAST_NAME_VALIDATION.MIN_MAX_LENGTH);
    } else if (!onlyAlphabetRegex.test(value)) {
      setLastNameError(LAST_NAME_VALIDATION.FORMAT);
    } else {
      setLastNameError('');
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    setDataManuallyEdited(true);

    if (value.trim() && !phoneRegex.test(value)) {
      setPhoneNumberError('Please enter a valid phone number.');
    } else {
      setPhoneNumberError('');
    }
  };

  const hasErrors = !!firstNameError || !!lastNameError || !!phoneNumberError || !!referralCodeError;

  const onAppleLoginSuccess = async (provider, data) => {
    dispatch(resetUserData());
    localStorage.clear();
    const idToken = data?.authorization?.id_token;
    try {
      const response = await realBricksApi.post(
        `${process.env.REACT_APP_API_URL}/api/v1/login/apple?loginType=Web`,
        {},
        {
          headers: {
            Authorization: `${idToken}`,
          },
        }
      );
      setResponseApple(response);
      if (response?.data?.data?.user?.isSignUp) {
        localStorage.setItem('email', response?.data?.data?.user?.email);
        localStorage.setItem('access_token', response?.data?.data?.accessToken);
        localStorage.setItem('access_token_temp', response?.data?.data?.accessToken);
        localStorage.setItem('counter', response?.data?.data?.counter);
        setSocialNameData(response?.data?.data?.user);
        if (response?.data?.data?.isNameSet === true) {
          if (response?.data?.data?.isPinSet === false) {
            navigate('/sign-up/createpin');
          } else {
            navigate('/login/enterpin');
          }
        } else {
          openNewSocialModal();
        }
      } else {
        localStorage.setItem('email', response?.data?.data?.email);
        localStorage.setItem('access_token', response?.data.data?.accessToken);
        localStorage.setItem('access_token_temp', response?.data.data?.accessToken);
        localStorage.setItem('counter', response?.data?.data?.counter);

        if (response?.data?.data?.isActive) {
          localStorage.setItem('email', response?.data?.data?.email);
          localStorage.setItem('access_token', response?.data?.data?.accessToken);
          localStorage.setItem('access_token_temp', response?.data.data?.accessToken);
          localStorage.setItem('counter', response?.data?.data?.counter);
          setSocialNameData(response?.data?.data);
          if (response?.data?.data?.isNameSet === true) {
            if (response?.data?.data?.isPinSet === false) {
              navigate('/sign-up/createpin');
            } else {
              navigate('/login/enterpin');
            }
          } else {
            openNewSocialModal();
          }
        } else {
          navigate('/');
          toastNotify('error', 'Something went wrong');
        }
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    }
  };

  const onResolve = ({ provider, data }) => {
    onAppleLoginSuccess(provider, data);
  };

  const onLoginSuccess = async (response) => {
    dispatch(resetUserData());
    localStorage.clear();
    const accessToken = response.access_token;
    try {
      const userResponse = await realBricksApi.post(
        `${process.env.REACT_APP_API_URL}/api/v1/login/google?loginType=Web`,
        {},
        {
          headers: {
            Authorization: `${accessToken}`,
          },
        }
      );
      setUserResponseGoogle(userResponse);

      if (userResponse.data.data?.user?.isSignUp) {
        localStorage.setItem('email', userResponse.data.data.user.email);
        localStorage.setItem('access_token', userResponse?.data?.data?.accessToken);
        localStorage.setItem('access_token_temp', userResponse?.data?.data?.accessToken);
        localStorage.setItem('counter', userResponse.data.data.counter);
        setSocialNameData(userResponse?.data?.data?.user);
        if (userResponse?.data?.data?.isNameSet === true) {
          if (userResponse?.data?.data?.isPinSet === false) {
            navigate('/sign-up/createpin');
          } else {
            navigate('/login/enterpin');
          }
        } else {
          openNewSocialModal();
        }
      } else {
        localStorage.setItem('access_token', userResponse?.data?.data?.accessToken);
        localStorage.setItem('access_token_temp', userResponse?.data?.data?.accessToken);
        localStorage.setItem('counter', userResponse.data.data.counter);
        localStorage.setItem('email', userResponse?.data?.data?.email);

        if (userResponse?.data?.data?.isActive) {
          localStorage.setItem('email', userResponse?.data?.data?.email);
          localStorage.setItem('access_token', userResponse?.data?.data?.accessToken);
          localStorage.setItem('counter', userResponse?.data?.data?.counter);
          setSocialNameData(userResponse?.data?.data);
          if (userResponse.data.data.isNameSet === true) {
            if (userResponse.data.data.isPinSet === false) {
              navigate('/sign-up/createpin');
            } else {
              navigate('/login/enterpin');
            }
          } else {
            openNewSocialModal();
          }
        } else {
          navigate('/');
          toastNotify('error', 'Something went wrong');
        }
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    }
  };

  const Googlelogin = useGoogleLogin({
    onSuccess: onLoginSuccess,
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    accessType: 'offline',
  });

  return (
    <div className='auth auth--details'>
      <AuthInfo
        className={viewForm ? 'hide' : null}
        title={<>Real estate investing for all</>}
        description={<>Get started today and begin building your wealth by investing in shares of fractionalized real estate.</>}
        note={
          <span className='underline cursor-pointer fw-600 auth__left__note' onClick={() => setDisclosureOpen(true)}>
            Company and Regulatory Disclosures
          </span>
        }
      >
        <div className='mobile-action' onClick={() => setViewForm(true)}>
          <Button className='w-100'>Get Started</Button>
        </div>
      </AuthInfo>
      <AuthForm
        className={viewForm ? 'show' : null}
        note={
          <>
            <span className='hide-mobile mr-4'>Already have an account?</span>
            <Link to='/login' className='underline fw-600'>
              Log in
            </Link>
          </>
        }
        title={
          <>
            <Hand verticalAlign='-3px' marginRight='8px' />
            Welcome to Realbricks.
          </>
        }
      >
        <Row gutter={[16, 16]} className='mb-32 pt-10'>
          <Col span={24} lg={12}>
            <Button type='primary' className='ant-btn-primary--alt icon-btn' block onClick={() => Googlelogin()}>
              <Google size={16} /> Continue with Google
            </Button>
          </Col>
          <Col span={24} lg={12}>
            <LoginSocialApple
              client_id={process.env.REACT_APP_APPLE_CLIENT_ID}
              // scope='name email'
              redirect_uri={process.env.REACT_APP_APPLE_REDIRECT_URL}
              onResolve={onResolve}
              onReject={(err) => {
                toastNotify('error', err);
              }}
            >
              <Button type='primary' className='ant-btn-primary--alt icon-btn' block>
                <Apple size={16} />
                Continue with Apple
              </Button>
            </LoginSocialApple>
          </Col>
        </Row>
        <Divider plain>OR</Divider>
        <div className='text-center'>
          <Link to={signupWithEmailUrl} className='ant-btn ant-btn-primary ant-btn-primary--alt icon-btn email'>
            <Email size={16} /> Sign up With Email
          </Link>
        </div>
      </AuthForm>
      <Modal
        className='ant-modal--title ant-modal--footer-shadow'
        title={<p className='mb-0 subhead-1'>Company and Regulatory Disclosures</p>}
        open={disclosureOpen}
        onOk={() => setDisclosureOpen(false)}
        onCancel={() => setDisclosureOpen(false)}
        footer={
          <Button type='primary' className='ant-btn-primary--alt' onClick={() => setDisclosureOpen(false)}>
            Done
          </Button>
        }
        width={652}
        closeIcon={<CloseIcon />}
        centered
      >
        <p>
          {' '}
          Terra Mint Group, Corp ("Terra Mint"), as the Managing Member of Neptune REM, LLC ("Neptune") operates the RealBricks.com website (the
          "Site") and is not a broker-dealer or investment advisor. All securities related activity is conducted through Dalmore Group LLC, a
          registered broker-dealer and member of FINRA/SIPC, located at 525 Green Pl. Woodmere, NY 11598. You can review the brokercheck for Dalmore.
          An up-to-date Dalmore Form CRS is available.
        </p>
        <p>
          You should speak with your financial advisor, accountant and/or attorney when evaluating any offering. Neither Terra Mint, Neptune, nor
          Dalmore makes any recommendations or provides advice about investments, and no communication, through this website or in any other medium,
          should be construed as a recommendation for any security offered on or off this investment platform. The Site may make forward-looking
          statements. You should not rely on these statements but should carefully evaluate the offering materials in assessing any investment
          opportunity, including the complete set of risk factors that are provided as part of the offering circular for your consideration.
        </p>
        <p>
          Neptune is conducting public offerings pursuant to Regulation A, as amended, through the Site. The most current offering circular for
          Neptune is available here. Past performance is no guarantee of future results. Investments such as those on the Realbricks platform are
          speculative and involve substantial risks to consider before investing. These risks are outlined in the respective offering materials and
          including, but not limited to, illiquidity and a complete loss of capital. Key risks include, but are not limited to, limited operating
          history, reliance on key personnel, inherent risks in investing in real estate, dividends may not be received on predictable schedule and or
          at all by Investors, and lack of voting rights. Also, the adverse economic effects of the COVID-19 pandemic remain unknown and could
          materially impact this investment. An investment in an offering constitutes only an investment in a particular series and not in the
          underlying asset(s). Investors should carefully review the risks located in the respective offering materials for a more comprehensive
          discussion of risk.
        </p>
        <p>
          From time to time, Neptune may undertake "testing the waters" for additional series of securities to be offered pursuant to Regulation A.
          For offerings that have not yet been qualified, no money or other consideration is being solicited, and if sent in response, will not be
          accepted.
        </p>
        <p>NO MONEY OR OTHER CONSIDERATION IS BEING SOLICITED, AND IF SENT IN RESPONSE, WILL NOT BE ACCEPTED.</p>
        <p>
          NO OFFER TO BUY THE SECURITIES CAN BE ACCEPTED AND NO PART OF THE PURCHASE PRICE CAN BE RECEIVED UNTIL THE OFFERING STATEMENT FILED BY THE
          COMPANY WITH THE SEC HAS BEEN QUALIFIED BY THE SEC. ANY SUCH OFFER MAY BE WITHDRAWN OR REVOKED, WITHOUT OBLIGATION OR COMMITMENT OF ANY
          KIND, AT ANY TIME BEFORE NOTICE OF ACCEPTANCE GIVEN AFTER THE DATE OF QUALIFICATION.
        </p>
        <p>AN INDICATION OF INTEREST INVOLVES NO OBLIGATION OR COMMITMENT OF ANY KIND.</p>
        <p>
          AN OFFERING STATEMENT REGARDING THIS OFFERING HAS BEEN FILED WITH THE SEC. YOU MAY OBTAIN A COPY OF THE PRELIMINARY OFFERING CIRCULAR THAT
          IS PART OF THAT OFFERING STATEMENT FROM: Neptune REM, LLC 30 N. Gould St. Suite R Sheridan, WY 82801 OR{' '}
          <a href='https://www.sec.gov/Archives/edgar/data/1992001/000110465923116338/xsl1-A_X01/primary_doc.xml'>
            <b>
              <u>HERE</u>
            </b>
          </a>
        </p>
        <p>
          Investment overviews contained herein contain summaries of the purpose and the principal business terms of the investment opportunities.
          Such summaries are intended for informational purposes only and do not purport to be complete, and each is qualified in its entirety by
          reference to the more-detailed discussions contained in the respective offering circular filed with the SEC.
        </p>
        <p>
          Realbricks does not offer refunds after an investment has been made. Please review the relevant offering materials and subscription
          documentation for more information.
        </p>
        <p>
          Potential investors should not rely on any forward-looking statements regarding any investment opportunity, which is based on our beliefs
          and information currently available to us. The words “anticipate,” “believe,” “expect,” “aim,” “potential,” “design,” “target,” “intend,”
          “may,” “might,” “plan,” “estimate,” “project,” “projection,” “should,” “will,” “would,” “result” and similar expressions identify
          forward-looking statements. Such statements are subject to risks, uncertainties, and assumptions and are not guarantees of future
          performance, which may be affected by known and unknown risks, trends, uncertainties, and factors that are beyond our control. These risks
          could result in the loss of your investment. See the offering materials for detailed information.
        </p>
        <p>
          Neptune does not have a public trading market for its Series Interests. While it intends to seek a quotation on PPEX, an alternative trading
          system (ATS) operated by North Capital Investment Technology, Inc. (“PPEX”) with a view to providing our holders of Series Interests with
          potential liquidity in the form of a secondary market for their investment in our Series Interests. However, there can be no guarantee that
          a secondary market may develop, or if it does, as to the volume or pricing with respect to any secondary trading that might develop. The
          PPEX does not employ market makers to provide liquidity, unlike national securities exchanges. Until Neptune’s Series Interests are listed,
          if ever, you may not sell the company’s Series Interests. Therefore, it may be difficult for you to sell Neptune’s Series Interests at the
          time you wish to do so, if you are able to sell them at all. If you are able to sell your Series Interests, you may have to sell them at a
          substantial discount to their public offering price. Because of the illiquid nature of our Series Interests, you should purchase Neptune’s
          Series Interests only as a long-term investment and be prepared to hold them for an indefinite period of time.
        </p>
        <p>
          Property information shown has been provided from various sources, which can include the seller and/or public records. It is believed
          reliable but not guaranteed and should not be relied upon without independent verification.
        </p>
        <p>
          Disclaimer: Images, Information and Floor Plan and Measurements are approximate and are for illustrative purposes only. We make no
          guarantee, warranty or representation as to the accuracy and completeness of the floor plans, measurements and or any information provided.
          You or your advisers should conduct a careful, independent investigation of the property to determine to your satisfaction as to the
          suitability of the property for your space requirements. All pricing and availability is subject to change. The information is to be used as
          a point of reference and not a binding agreement.
        </p>
      </Modal>

      <Modal
        open={newModalVisible}
        onCancel={closeNewSocialModal}
        footer={
          <Button type='primary' className='ant-btn-primary--alt' onClick={handleDoneClick} disabled={hasErrors}>
            Proceed
          </Button>
        }
      >
        <div>
          <p style={{ marginBottom: '8px', fontWeight: 'bold' }}>
            Note: Enter your first and last name as it appears on your government issued ID.
            <Tooltip
              placement='bottomRight'
              title={
                <>
                  <p>A government issued ID could be your Passport, Driver’s License, National ID, or Permanent Resident Card.</p>
                  <p className='mb-0'> We are required by federal law to verify the identity of any person seeking to open a financial account.</p>
                </>
              }
            >
              {' '}
              <Info marginLeft='7px' top='2px' />{' '}
            </Tooltip>
          </p>
          <div>
            <label htmlFor='firstName'>First Name:</label>
            <Input id='firstName' value={firstName} onChange={handleFirstNameChange} placeholder='First Name' />
            {firstNameError && <span style={{ color: 'red' }}>{firstNameError}</span>}
          </div>
        </div>
        <div>
          <label htmlFor='lastName'>Last Name:</label>
          <Input id='lastName' value={lastName} onChange={handleLastNameChange} placeholder='Last Name' />
          {lastNameError && <span style={{ color: 'red' }}>{lastNameError}</span>}
        </div>
        <div>
          <label htmlFor='phoneNumber'>Phone Number:</label>
          <Input type='tel' id='referralCode' value={phoneNumber} onChange={handleChangePhoneNumber} placeholder='(XXX) XXX-XXXX' />
          {phoneNumberError && <span style={{ color: 'red' }}>{phoneNumberError}</span>}
        </div>

        <div>
          <label htmlFor='referredBy'>Referral Code:</label>
          <Input
            id='referredBy'
            value={referralLink || referredBy}
            onChange={handleReferralCodeChange}
            placeholder='Referral Code'
            disabled={!!referralLink}
          />
          {referralCodeError && <span style={{ color: 'red' }}>{referralCodeError}</span>}
        </div>
        <div className='mt-20'>
          <Checkbox
            checked={subscribeForSMS && phoneNumber.trim() !== ''}
            onChange={(e) => setSubscribeForSMS(e.target.checked && phoneNumber.trim() !== '')}
            disabled={!phoneNumber.trim()}
          >
            Opt-in to be the first to know about new properties. Signup for text messages for early access.
          </Checkbox>
        </div>
      </Modal>
    </div>
  );
};
